import {
  type TouchEvent as ReactTouchEvent,
  type MouseEvent as ReactMouseEvent,
  type MouseEventHandler as ReactMouseEventHandler,
  type TouchEventHandler as ReactTouchEventHandler,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAreaSelectGesture } from "../../FloorPlanPanelNew/hooks/useAreaSelectGesture";
import { normalizeWheelDelta } from "utils/math/normalizeWheelDelta";
import { ZOOM_CONFIG } from "../../FloorPlanPanelNew/constants";
import { findClickedRoute } from "../../FloorPlanPanelNew/utils/findClickedRoute";
import type { UseMapInteractionArgs, UseMapInteractionResult } from "./types";

const getTouchDistance = (
  touches: ReactTouchEvent<HTMLDivElement>["touches"],
): number => {
  if (touches.length < 2) return 0;
  const dx = touches[0].clientX - touches[1].clientX;
  const dy = touches[0].clientY - touches[1].clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchCenter = (
  touches: ReactTouchEvent<HTMLDivElement>["touches"],
): { x: number; y: number } => {
  if (touches.length === 1) {
    return { x: touches[0].clientX, y: touches[0].clientY };
  }
  const touchCenterX = (touches[0].clientX + touches[1].clientX) / 2;
  const touchCenterY = (touches[0].clientY + touches[1].clientY) / 2;
  return { x: touchCenterX, y: touchCenterY };
};

export const useMapInteraction = ({
  containerRef,
  zoomLevel,
  setZoomLevel,
  panOffset,
  setPanOffset,
  minZoomLevel,
  objects,
  onObjectClick,
  isPickZoneMode,
  onMapClick,
  onMapMouseMove,
  onAreaSelect,
  onEmptyMapClick,
  isReady,
  circleSizeScale = 1,
  circuitPaths,
}: UseMapInteractionArgs): UseMapInteractionResult => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [isHoveringObject, setIsHoveringObject] = useState(false);
  const [isHoveringPlan, setIsHoveringPlan] = useState(false);
  const dragStartCoords = useRef({ x: 0, y: 0 });
  const areaSelect = useAreaSelectGesture({ containerRef, panOffset, zoomLevel, onAreaSelect });

  // Touch state
  const [isTouching, setIsTouching] = useState(false);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  const [initialPinchDistance, setInitialPinchDistance] = useState(0);
  const [initialZoom, setInitialZoom] = useState(1);
  const touchStartCoords = useRef({ x: 0, y: 0 });

  // Wheel zoom tracking
  const [isWheelZooming, setIsWheelZooming] = useState(false);
  const wheelZoomTimeoutRef = useRef<number | null>(null);

  // Ref to avoid stale closure for flashTimeout check
  const flashTimeoutActiveRef = useRef(false);

  const handleObjectClickInternal = useCallback(
    (event: {
      clientX: number;
      clientY: number;
      preventDefault: () => void;
      stopPropagation: () => void;
    }) => {
      if (flashTimeoutActiveRef.current) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const localX = event.clientX - rect.left;
      const localY = event.clientY - rect.top;

      const planX = (localX - panOffset.x) / zoomLevel;
      const planY = (localY - panOffset.y) / zoomLevel;

      const clickedObject = objects.find(
        (obj) =>
          planX >= obj.bbox.x1 &&
          planX <= obj.bbox.x2 &&
          planY >= obj.bbox.y1 &&
          planY <= obj.bbox.y2,
      );

      if (isPickZoneMode) {
        onMapClick?.({ x: planX, y: planY });
        return;
      }

      if (clickedObject) {
        onObjectClick(clickedObject.id);
        return;
      }

      if (circuitPaths) {
        const clickedRouteId = findClickedRoute({
          paths: circuitPaths.paths,
          planX,
          planY,
          zoomLevel,
          circleSizeScale,
        });
        if (clickedRouteId) {
          onObjectClick(clickedRouteId);
          return;
        }
      }

      onEmptyMapClick?.({ x: planX, y: planY });
    },
    [
      containerRef,
      panOffset.x,
      panOffset.y,
      zoomLevel,
      objects,
      isPickZoneMode,
      circleSizeScale,
      onMapClick,
      onEmptyMapClick,
      onObjectClick,
      circuitPaths,
    ],
  );

  const handleZoom = useCallback(
    (
      directionOrEvent: string | WheelEvent,
      mousePosition: { x: number; y: number } | null = null,
    ) => {
      if (!containerRef.current) return;

      let delta;
      let zoomPoint = mousePosition;

      if (typeof directionOrEvent === "string") {
        delta =
          directionOrEvent === "in"
            ? ZOOM_CONFIG.BUTTON_IN_FACTOR
            : ZOOM_CONFIG.BUTTON_OUT_FACTOR;
      } else if (directionOrEvent && directionOrEvent.deltaY !== undefined) {
        const event = directionOrEvent;
        delta = -normalizeWheelDelta({
          e: event,
          pixelMultiplier: ZOOM_CONFIG.WHEEL_PIXEL_MODE_MULTIPLIER,
          lineMultiplier: ZOOM_CONFIG.WHEEL_LINE_MODE_MULTIPLIER,
          maxDelta: ZOOM_CONFIG.WHEEL_MAX_DELTA,
        });
        if (!zoomPoint) {
          zoomPoint = { x: event.clientX, y: event.clientY };
        }
      } else {
        console.warn("Invalid zoom input");
        return;
      }

      const isButtonZoom = typeof directionOrEvent === "string";
      const factor = Math.exp(
        isButtonZoom ? delta : delta * ZOOM_CONFIG.WHEEL_SENSITIVITY,
      );
      const newZoom = zoomLevel * factor;
      const clampedZoom = Math.max(
        minZoomLevel.current,
        Math.min(ZOOM_CONFIG.MANUAL_MAX, newZoom),
      );

      const containerRect = containerRef.current.getBoundingClientRect();
      const mx = zoomPoint
        ? zoomPoint.x - containerRect.left
        : containerRect.width / 2;
      const my = zoomPoint
        ? zoomPoint.y - containerRect.top
        : containerRect.height / 2;

      const imageX = (mx - panOffset.x) / zoomLevel;
      const imageY = (my - panOffset.y) / zoomLevel;

      const newPanX = mx - imageX * clampedZoom;
      const newPanY = my - imageY * clampedZoom;

      setZoomLevel(clampedZoom);
      setPanOffset({ x: newPanX, y: newPanY });
    },
    [
      zoomLevel,
      panOffset,
      containerRef,
      minZoomLevel,
      setZoomLevel,
      setPanOffset,
    ],
  );

  // --- Mouse handlers ---
  const handleContainerMouseMove: ReactMouseEventHandler<HTMLDivElement> = (
    event,
  ) => {
    if (areaSelect.handleMouseMove(event)) return;
    if (!containerRef.current || isDragging || !isReady) return;

    const containerRect = containerRef.current.getBoundingClientRect();
    let moveX = event.clientX - containerRect.left;
    let moveY = event.clientY - containerRect.top;

    moveX = (moveX - panOffset.x) / zoomLevel;
    moveY = (moveY - panOffset.y) / zoomLevel;

    const isOverObject = objects.some(
      (obj) =>
        moveX >= obj.bbox.x1 &&
        moveX <= obj.bbox.x2 &&
        moveY >= obj.bbox.y1 &&
        moveY <= obj.bbox.y2,
    );

    if (isOverObject !== isHoveringObject) {
      setIsHoveringObject(isOverObject);
    }

    onMapMouseMove?.({ x: moveX, y: moveY });
  };

  const handleMouseDown: ReactMouseEventHandler<HTMLDivElement> = (event) => {
    if (areaSelect.handleMouseDown(event)) return;
    if (event.button === 0 || event.button === 1) {
      setIsDragging(true);
      setDragStart({
        x: event.clientX - panOffset.x,
        y: event.clientY - panOffset.y,
      });
      dragStartCoords.current = { x: event.clientX, y: event.clientY };
      event.preventDefault();
      event.stopPropagation();
    }
  };

  const handleMouseMove = useCallback(
    (event: MouseEvent | ReactMouseEvent<HTMLDivElement>) => {
      if (isDragging) {
        setPanOffset({
          x: event.clientX - dragStart.x,
          y: event.clientY - dragStart.y,
        });
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, dragStart, setPanOffset],
  );

  const handleMouseUp = useCallback(
    (event: MouseEvent | ReactMouseEvent<HTMLDivElement>) => {
      if (areaSelect.handleMouseUp(event, () => handleObjectClickInternal(event))) return;
      if (isDragging) {
        setIsDragging(false);
        const dx = Math.abs(event.clientX - dragStartCoords.current.x);
        const dy = Math.abs(event.clientY - dragStartCoords.current.y);
        if (dx < 5 && dy < 5) {
          handleObjectClickInternal(event);
        }
        event.preventDefault();
        event.stopPropagation();
      }
    },
    [isDragging, handleObjectClickInternal, areaSelect],
  );

  const handleMouseEnter = () => setIsHoveringPlan(true);
  const handleMouseLeave = () => {
    setIsDragging(false);
    areaSelect.cancel();
    setIsHoveringPlan(false);
    onMapMouseMove?.(null);
  };

  // --- Touch handlers ---
  const handleTouchStart: ReactTouchEventHandler<HTMLDivElement> = (event) => {
    event.preventDefault();
    if (event.touches.length === 1) {
      setIsTouching(true);
      const touch = event.touches[0];
      setTouchStart({
        x: touch.clientX - panOffset.x,
        y: touch.clientY - panOffset.y,
      });
      touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
    } else if (event.touches.length === 2) {
      setIsTouching(false);
      setInitialPinchDistance(getTouchDistance(event.touches));
      setInitialZoom(zoomLevel);
    }
  };

  const handleTouchMove = useCallback(
    (event: Event | ReactTouchEvent<HTMLDivElement>) => {
      event.preventDefault();
      if ("touches" in event && event.touches.length === 1 && isTouching) {
        const touch = event.touches[0];
        setPanOffset({
          x: touch.clientX - touchStart.x,
          y: touch.clientY - touchStart.y,
        });
      } else if ("touches" in event && event.touches.length === 2) {
        const distance = getTouchDistance(event.touches);
        const center = getTouchCenter(event.touches);

        if (initialPinchDistance > 0) {
          const scale = distance / initialPinchDistance;
          const newZoom = Math.max(
            0.1,
            Math.min(ZOOM_CONFIG.MANUAL_MAX, initialZoom * scale),
          );

          const containerRect = containerRef.current?.getBoundingClientRect();
          if (containerRect) {
            const pinchCenter = {
              x: center.x - containerRect.left,
              y: center.y - containerRect.top,
            };
            const zoomFactor = newZoom / zoomLevel;
            const newPanX =
              pinchCenter.x - (pinchCenter.x - panOffset.x) * zoomFactor;
            const newPanY =
              pinchCenter.y - (pinchCenter.y - panOffset.y) * zoomFactor;

            setZoomLevel(newZoom);
            setPanOffset({ x: newPanX, y: newPanY });
          }
        }
      }
    },
    [
      isTouching,
      touchStart,
      initialPinchDistance,
      initialZoom,
      zoomLevel,
      panOffset,
      containerRef,
      setZoomLevel,
      setPanOffset,
    ],
  );

  const handleTouchEnd = useCallback(
    (event: Event | ReactTouchEvent<HTMLDivElement>) => {
      if ("touches" in event && event.touches.length === 0) {
        if (isTouching) {
          setIsTouching(false);
          const touch = event.changedTouches[0];
          const dx = Math.abs(touch.clientX - touchStartCoords.current.x);
          const dy = Math.abs(touch.clientY - touchStartCoords.current.y);
          if (dx < 10 && dy < 10) {
            handleObjectClickInternal({
              clientX: touch.clientX,
              clientY: touch.clientY,
              preventDefault: () => {},
              stopPropagation: () => {},
            });
          }
        }
        setInitialPinchDistance(0);
        setInitialZoom(1);
      } else if ("touches" in event && event.touches.length === 1) {
        setInitialPinchDistance(0);
        if (!isTouching) {
          const touch = event.touches[0];
          setIsTouching(true);
          setTouchStart({
            x: touch.clientX - panOffset.x,
            y: touch.clientY - panOffset.y,
          });
          touchStartCoords.current = { x: touch.clientX, y: touch.clientY };
        }
      }
    },
    [isTouching, handleObjectClickInternal, panOffset],
  );

  const handleWheel = useCallback(
    (event: WheelEvent) => {
      event.preventDefault();
      setIsWheelZooming(true);
      if (wheelZoomTimeoutRef.current !== null) {
        clearTimeout(wheelZoomTimeoutRef.current);
      }
      wheelZoomTimeoutRef.current = window.setTimeout(() => {
        setIsWheelZooming(false);
        wheelZoomTimeoutRef.current = null;
      }, 150);
      handleZoom(event);
    },
    [handleZoom],
  );

  // --- Document event listeners ---
  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener("wheel", handleWheel, { passive: false });
      return () => container.removeEventListener("wheel", handleWheel);
    }
  }, [handleWheel, containerRef]);

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDragging, dragStart]);

  useEffect(() => {
    if (isTouching) {
      document.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      });
      document.addEventListener("touchend", handleTouchEnd, {
        passive: false,
      });
      return () => {
        document.removeEventListener("touchmove", handleTouchMove);
        document.removeEventListener("touchend", handleTouchEnd);
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTouching, touchStart]);

  useEffect(() => {
    return () => {
      if (wheelZoomTimeoutRef.current !== null) {
        clearTimeout(wheelZoomTimeoutRef.current);
      }
    };
  }, []);

  return {
    containerEventHandlers: {
      onMouseDown: handleMouseDown,
      onMouseUp: handleMouseUp,
      onMouseMove: handleContainerMouseMove,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onTouchStart: handleTouchStart,
      onTouchMove: handleTouchMove,
      onTouchEnd: handleTouchEnd,
    },
    handleZoom,
    isDragging,
    isTouching,
    isWheelZooming,
    isHoveringObject,
    isHoveringPlan,
    selectionBox: areaSelect.selectionBox,
    isAreaSelecting: Boolean(onAreaSelect) && !areaSelect.isSpaceDown,
  };
};
